import { components as c } from "@/components";
import { SurgeryCliniciansFormFields } from "@/features/events/features/surgeries/components/SurgeryCliniciansFormFields";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { SubmitHandler, useForm } from "react-hook-form";
import { useSurgeryMutation } from "@/features/events/features/surgeries/api/upsertSurgery";
import { z } from "zod";
import { diaryByIdQueryOptions, useDiaryByIdQuery } from "@/features/diaries";
import { extractSurgeryById } from "@/features/events/features/surgeries/hooks/useExtractSurgery";
import { useEventStepper } from "@/features/templates/hooks/useEventNavigationStore";

const surgeryCliniciansZ = z.object({
    event_id: z.string().uuid(),
    surgery_id: z.string().uuid(),
    operating_clinician_id: z
        .string()
        .uuid()
        .nullable()
        .optional()
        .transform((value) => (value === "" ? null : value)),
    referring_clinician_id: z
        .string()
        .uuid()
        .nullable()
        .optional()
        .transform((value) => (value === "" ? null : value)),
});

type SurgeryCliniciansT = z.infer<typeof surgeryCliniciansZ>;

const SurgeryFormFieldsPage = () => {
    const { diaryId, eventId, surgeryId } = Route.useParams();

    const { data: diary } = useDiaryByIdQuery(diaryId);

    const { surgery } = Route.useLoaderData();

    const surgeryMutation = useSurgeryMutation({ diaryId });

    const navigate = useNavigate();

    const form = useForm<SurgeryCliniciansT>({
        resolver: zodResolver(surgeryCliniciansZ),
        defaultValues: {
            event_id: eventId,
            surgery_id: surgeryId,
            operating_clinician_id: surgery?.operating_clinician_id ?? null,
            referring_clinician_id: surgery?.referring_clinician_id ?? null,
        },
    });

    console.log(form.formState.errors);

    const onSubmit: SubmitHandler<SurgeryCliniciansT> = async (formData) => {
        surgeryMutation.mutate(formData, {
            onSuccess: () => {
                navigate({
                    to: "/templates/diary/$diaryId/events/$eventId/surgery/$surgeryId/details",
                    params: { diaryId, eventId, surgeryId },
                });
            },
        });
    };

    const handlePrevious = () => {
        navigate({
            to: "/templates/diary/$diaryId/events/eventId/details",
            params: { diaryId, eventId },
        });
    };

    const { steps } = useEventStepper("surgery");

    return (
        <>
            <c.cc.Stepper steps={steps} />
            <c.ui.Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <SurgeryCliniciansFormFields organisationId={diary?.organisation_id as string} />

                    {/* Navigation Buttons */}
                    <div className="flex justify-between gap-4 mt-8">
                        <c.fc.BackButton
                            type="button"
                            variant="outline"
                            label="Previous"
                            onClick={handlePrevious}
                            className="w-auto"
                        />

                        <c.fc.NextButton />
                    </div>
                </form>
            </c.ui.Form>
        </>
    );
};

export const Route = createFileRoute("/_app/templates/diary/$diaryId/events/$eventId/surgery/$surgeryId/")({
    component: SurgeryFormFieldsPage,
    loader: async ({
        context,
        params,
    }: {
        context: any;
        params: { diaryId: string; eventId: string; surgeryId: string };
    }) => {
        const { diaryId, eventId, surgeryId } = params;

        const diary = await context.queryClient.ensureQueryData(diaryByIdQueryOptions(diaryId));
        if (!diary) {
            return { event: undefined, organisationId: undefined };
        }
        const surgery = diary ? extractSurgeryById(diary, eventId, surgeryId) : undefined;
        return { surgery, organisationId: diary.organisation_id };
    },
});
